
import { useEffect, useMemo, useState } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Copy, Edit, Eye, FolderOpen, MessageCircle, Plus, Search, Trash2 } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { db, storage } from '../../core/firebase/firebase';

type OwnerType = 'client' | 'supplier' | 'staff' | 'booking' | 'general';
type DocumentType = 'passport' | 'cc_copy' | 'visa' | 'id' | 'ticket' | 'insurance' | 'invoice' | 'receipt' | 'refund' | 'voucher' | 'supplier_confirmation' | 'hotel_confirmation' | 'cruise_confirmation' | 'other';
type OwnerOption = { id: string; name: string; department?: string };
type DocRec = {
  id: string; documentCode: string; ownerType: OwnerType; ownerId: string; ownerName: string; ownerDepartment: string;
  documentType: DocumentType; documentFor: string; fileName: string; fileUrl: string; filePath: string; mimeType: string;
  visibility: 'general' | 'owner_department' | 'admin_only'; status: 'active' | 'archived'; notes: string; createdBy: string; updatedBy: string;
};

const ownerTypes: { value: OwnerType; label: string }[] = [
  { value: 'client', label: 'Client' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'staff', label: 'Staff' },
  { value: 'booking', label: 'Booking' },
  { value: 'general', label: 'General' }
];

const documentTypes: { value: DocumentType; label: string }[] = [
  { value: 'passport', label: 'Passport' },
  { value: 'cc_copy', label: 'CC Copy' },
  { value: 'visa', label: 'Visa' },
  { value: 'id', label: 'ID' },
  { value: 'ticket', label: 'Ticket' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'refund', label: 'Refund' },
  { value: 'voucher', label: 'Voucher' },
  { value: 'supplier_confirmation', label: 'Supplier Confirmation' },
  { value: 'hotel_confirmation', label: 'Hotel Confirmation' },
  { value: 'cruise_confirmation', label: 'Cruise Confirmation' },
  { value: 'other', label: 'Other' }
];

const makeCode = () => `DOC-${Math.floor(100000 + Math.random() * 900000)}`;

function normalizeDoc(id: string, raw: any): DocRec {
  return {
    id,
    documentCode: String(raw.documentCode || makeCode()),
    ownerType: (raw.ownerType || 'general') as OwnerType,
    ownerId: String(raw.ownerId || ''),
    ownerName: String(raw.ownerName || ''),
    ownerDepartment: String(raw.ownerDepartment || ''),
    documentType: (raw.documentType || 'other') as DocumentType,
    documentFor: String(raw.documentFor || raw.title || ''),
    fileName: String(raw.fileName || ''),
    fileUrl: String(raw.fileUrl || ''),
    filePath: String(raw.filePath || ''),
    mimeType: String(raw.mimeType || ''),
    visibility: raw.visibility || (raw.ownerType === 'general' ? 'general' : 'owner_department'),
    status: raw.status || 'active',
    notes: String(raw.notes || ''),
    createdBy: String(raw.createdBy || ''),
    updatedBy: String(raw.updatedBy || '')
  };
}

async function loadClients(): Promise<OwnerOption[]> {
  const snap = await getDocs(collection(db, 'clients'));
  return snap.docs.map((d) => {
    const x = d.data() as any;
    const first = String(x.firstName || '').trim();
    const last = String(x.lastName || '').trim();
    const combined = `${first} ${last}`.trim();
    return { id: d.id, name: String(x.fullName || x.name || combined || x.phoneFull || x.phoneNumber || d.id), department: String(x.department || x.primaryDepartment || 'general') };
  }).filter((x) => !!x.name);
}

async function loadSuppliers(): Promise<OwnerOption[]> {
  const snap = await getDocs(collection(db, 'suppliers'));
  return snap.docs.map((d) => {
    const x = d.data() as any;
    return { id: d.id, name: String(x.supplierName || x.companyName || x.name || d.id), department: String(x.department || x.primaryDepartment || 'general') };
  }).filter((x) => !!x.name);
}

async function loadStaff(): Promise<OwnerOption[]> {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => {
    const x = d.data() as any;
    return { id: d.id, name: String(x.fullName || x.name || x.email || d.id), department: String(x.department || x.primaryDepartment || (Array.isArray(x.departments) ? x.departments[0] : '') || 'general') };
  }).filter((x) => !!x.name);
}

async function loadDocuments(): Promise<DocRec[]> {
  try {
    const snap = await getDocs(query(collection(db, 'documents'), orderBy('updatedAt', 'desc')));
    return snap.docs.map((d) => normalizeDoc(d.id, d.data()));
  } catch {
    const snap = await getDocs(collection(db, 'documents'));
    return snap.docs.map((d) => normalizeDoc(d.id, d.data()));
  }
}

function canSeeDocument(item: DocRec, user: ERPUser) {
  if (item.ownerType === 'general' || item.visibility === 'general') return true;
  if (user.role === 'super_admin' || user.role === 'admin') return true;
  if (item.ownerType === 'staff' && item.ownerId === user.uid) return true;
  if (item.ownerDepartment && item.ownerDepartment === user.department) return true;
  return false;
}

export default function DocumentsPage({ user }: { user: ERPUser }) {
  const [documents, setDocuments] = useState<DocRec[]>([]);
  const [clients, setClients] = useState<OwnerOption[]>([]);
  const [suppliers, setSuppliers] = useState<OwnerOption[]>([]);
  const [staff, setStaff] = useState<OwnerOption[]>([]);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<'all' | OwnerType>('all');
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<DocRec | null>(null);
  const [editing, setEditing] = useState<DocRec | null>(null);
  const [ownerType, setOwnerType] = useState<OwnerType>('client');
  const [ownerId, setOwnerId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerDepartment, setOwnerDepartment] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('passport');
  const [documentFor, setDocumentFor] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadAll() {
    setMessage('');
    const [docRows, clientRows, supplierRows, staffRows] = await Promise.all([
      loadDocuments(),
      loadClients().catch(() => []),
      loadSuppliers().catch(() => []),
      loadStaff().catch(() => [])
    ]);
    setDocuments(docRows);
    setClients(clientRows);
    setSuppliers(supplierRows);
    setStaff(staffRows);
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qOwnerType = params.get('ownerType') as OwnerType | null;
    if (qOwnerType && ['client','supplier','staff','general'].includes(qOwnerType)) {
      setOwnerFilter(qOwnerType);
    }
    loadAll().catch((error) => setMessage(error instanceof Error ? error.message : 'Unable to load documents.'));
  }, []);

  const ownerOptions = useMemo(() => {
    if (ownerType === 'client') return clients;
    if (ownerType === 'supplier') return suppliers;
    if (ownerType === 'staff') return staff;
    return [];
  }, [ownerType, clients, suppliers, staff]);

  const visibleDocuments = useMemo(() => {
    const q = search.toLowerCase();
    return documents
      .filter((item) => canSeeDocument(item, user))
      .filter((item) => ownerFilter === 'all' || item.ownerType === ownerFilter)
      .filter((item) => !q || [item.documentCode, item.ownerType, item.ownerName, item.documentType, item.documentFor, item.fileName, item.notes].join(' ').toLowerCase().includes(q));
  }, [documents, search, ownerFilter, user]);

  function resetForm() {
    setEditing(null);
    setOwnerType('client');
    setOwnerId('');
    setOwnerName('');
    setOwnerDepartment('');
    setDocumentType('passport');
    setDocumentFor('');
    setNotes('');
    setFiles([]);
    setMessage('');
  }

  function startAdd() { resetForm(); setViewing(null); setOpen(true); }

  function startEdit(item: DocRec) {
    setViewing(null);
    setEditing(item);
    setOwnerType(item.ownerType);
    setOwnerId(item.ownerId);
    setOwnerName(item.ownerName);
    setOwnerDepartment(item.ownerDepartment);
    setDocumentType(item.documentType);
    setDocumentFor(item.documentFor);
    setNotes(item.notes);
    setFiles([]);
    setMessage('');
    setOpen(true);
  }

  function handleOwnerTypeChange(value: OwnerType) {
    setOwnerType(value);
    setOwnerId('');
    setOwnerName('');
    setOwnerDepartment('');
  }

  function handleOwnerChange(id: string) {
    setOwnerId(id);
    const selected = ownerOptions.find((item) => item.id === id);
    setOwnerName(selected?.name || '');
    setOwnerDepartment(selected?.department || '');
  }

  async function uploadSingleFile(file: File) {
    const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const ownerFolder = ownerType === 'general' ? 'general' : ownerId || 'unlinked';
    const path = `documents/${ownerType}/${ownerFolder}/${Date.now()}_${cleanName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const fileUrl = await getDownloadURL(storageRef);

    await addDoc(collection(db, 'documents'), {
      documentCode: makeCode(),
      ownerType,
      ownerId: ownerType === 'general' ? '' : ownerId,
      ownerName: ownerType === 'general' ? 'General' : ownerName,
      ownerDepartment: ownerType === 'general' ? '' : ownerDepartment,
      documentType,
      documentFor: documentFor || file.name,
      fileName: file.name,
      fileUrl,
      filePath: path,
      mimeType: file.type,
      visibility: ownerType === 'general' ? 'general' : 'owner_department',
      status: 'active',
      notes,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid,
      updatedBy: user.uid
    });
  }

  async function saveEditOnly() {
    if (!editing) return;
    if (ownerType !== 'general' && !ownerId) { setMessage('Please select owner name first.'); return; }
    if (!documentFor.trim()) { setMessage('Document For is required.'); return; }

    setBusy(true);
    try {
      await updateDoc(doc(db, 'documents', editing.id), {
        ownerType,
        ownerId: ownerType === 'general' ? '' : ownerId,
        ownerName: ownerType === 'general' ? 'General' : ownerName,
        ownerDepartment: ownerType === 'general' ? '' : ownerDepartment,
        documentType,
        documentFor,
        visibility: ownerType === 'general' ? 'general' : 'owner_department',
        notes,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
      setOpen(false);
      setEditing(null);
      await loadAll();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update document.');
    } finally {
      setBusy(false);
    }
  }

  async function submitDocuments() {
    if (editing) { await saveEditOnly(); return; }
    if (ownerType !== 'general' && !ownerId) { setMessage(`Please select ${ownerType === 'client' ? 'Client' : ownerType === 'supplier' ? 'Supplier' : 'Staff'} name first.`); return; }
    if (!documentFor.trim()) { setMessage('Document For is required.'); return; }
    if (!files.length) { setMessage('Please select at least one file.'); return; }

    setBusy(true); setMessage('');
    try {
      for (const file of files) await uploadSingleFile(file);
      setOpen(false);
      await loadAll();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to upload document.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div><p className="eyebrow">Phase 5.4</p><h1>Documents Final System</h1><p>Upload and manage documents for Client, Supplier, Staff, or General records.</p></div>
        <button className="btn primary" title="Add new document upload" onClick={startAdd}><Plus size={18}/> Add Document</button>
      </div>

      {message && <div className="error-box" style={{marginBottom:20}}>{message}</div>}

      <div className="toolbar">
        <div style={{position:'relative'}}>
          <Search size={18} style={{position:'absolute',left:16,top:18,color:'#66758a'}}/>
          <input style={{paddingLeft:44}} placeholder="Search owner, document for, type, file name" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value as 'all' | OwnerType)}>
          <option value="all">All Owner Types</option>
          {ownerTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </div>

      <section className="card table-card" style={{marginTop:20}}>
        <table>
          <thead><tr><th>Document For</th><th>Owner Type</th><th>Owner Name</th><th>Type</th><th>File</th><th>Visibility</th><th>Actions</th></tr></thead>
          <tbody>
            {visibleDocuments.length === 0 && <tr><td colSpan={7} className="empty">No documents found.</td></tr>}
            {visibleDocuments.map((item) => (
              <tr key={item.id}>
                <td><strong>{item.documentFor}</strong><br/><small>{item.documentCode}</small></td>
                <td>{item.ownerType}</td>
                <td>{item.ownerName || '-'}</td>
                <td>{item.documentType}</td>
                <td>{item.fileName || '-'}</td>
                <td><span className="pill">{item.visibility}</span></td>
                <td className="actions">
                  <button title="View document details" onClick={() => setViewing(item)}><Eye size={16}/></button>
                  <button title="Open document file" onClick={() => item.fileUrl ? window.open(item.fileUrl, '_blank') : alert('No file URL found.')}><FolderOpen size={16}/></button>
                  <button title="Copy document link" onClick={async () => { if(item.fileUrl){ await navigator.clipboard.writeText(item.fileUrl); setMessage('Document link copied.'); } }}><Copy size={16}/></button>
                  <button title="Share document on WhatsApp" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Sardar Patel Travel - ' + item.documentFor + '\n' + item.fileUrl)}`, '_blank')}><MessageCircle size={16}/></button>
                  <button title="Edit document record" onClick={() => startEdit(item)}><Edit size={16}/></button>
                  <button title="Delete document" onClick={async () => { if(confirm('Delete this document record?')) { await deleteDoc(doc(db, 'documents', item.id)); await loadAll(); } }}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {open && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{editing ? 'Edit Document' : 'Add Document'}</h2>
              <button className="icon-only" title="Close" onClick={() => setOpen(false)}>×</button>
            </div>

            <div className="form-section-title">Owner Type Selection</div>
            <div className="form-grid">
              <label>Owner Type
                <select value={ownerType} onChange={(e) => handleOwnerTypeChange(e.target.value as OwnerType)}>
                  {ownerTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>

              {ownerType !== 'general' && (
                <label>
                  {ownerType === 'client' ? 'Client Name' : ownerType === 'supplier' ? 'Supplier Name' : 'Staff Name'}
                  <select value={ownerId} onChange={(e) => handleOwnerChange(e.target.value)}>
                    <option value="">Select {ownerType}</option>
                    {ownerOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
              )}

              {ownerType !== 'general' && <label>Selected Name<input value={ownerName} readOnly /></label>}
              {ownerType === 'general' && <label>Selected Area<input value="General Document" readOnly /></label>}

              <label>Type
                <select value={documentType} onChange={(e) => setDocumentType(e.target.value as DocumentType)}>
                  {documentTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </label>

              <label className="span-2">Document For *
                <input placeholder="Example: Passport front, Visa copy, Company W9, Staff ID" value={documentFor} onChange={(e) => setDocumentFor(e.target.value)} />
              </label>

              {!editing && (
                <label className="span-3">Upload File(s) *
                  <input multiple type="file" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </label>
              )}

              {editing && (
                <label className="span-3">Existing File
                  <input value={editing.fileName || editing.fileUrl || 'Saved file'} readOnly />
                </label>
              )}

              <label className="span-3">Notes
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
              </label>
            </div>

            <div className="modal-foot">
              <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn primary" disabled={busy} onClick={submitDocuments}>
                {busy ? 'Saving...' : editing ? 'Update Document' : files.length > 1 ? `Submit ${files.length} Documents` : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>{viewing.documentFor}</h2>
              <button className="icon-only" title="Close" onClick={() => setViewing(null)}>×</button>
            </div>
            <div className="form-section-title">Document Details</div>
            <div className="form-grid">
              <div><b>Document Code</b><p>{viewing.documentCode}</p></div>
              <div><b>Owner Type</b><p>{viewing.ownerType}</p></div>
              <div><b>Owner Name</b><p>{viewing.ownerName || '-'}</p></div>
              <div><b>Type</b><p>{viewing.documentType}</p></div>
              <div><b>File Name</b><p>{viewing.fileName || '-'}</p></div>
              <div><b>Visibility</b><p>{viewing.visibility}</p></div>
              <div className="span-3"><b>File URL</b><p>{viewing.fileUrl || '-'}</p></div>
              <div className="span-3"><b>Notes</b><p>{viewing.notes || '-'}</p></div>
            </div>
            <div className="modal-foot">
              <button className="btn" title="Open uploaded document" onClick={() => viewing.fileUrl ? window.open(viewing.fileUrl, '_blank') : alert('No file URL found.')}>Open File</button>
              <button className="btn primary" title="Edit document record" onClick={() => startEdit(viewing)}>Edit Document</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
